import React from 'react';

import { createRoot } from 'react-dom/client';

import { $config } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import {
  AdServer,
  ContainerId,
  type PlacementId,
  PlacementStatus,
  debugLog
} from '@schibsted-nmp/advertising-shared';

import { SponsoredProductCarousel } from '../ads/SponsoredProductCarousel.js';
import {
  DsaBrand,
  type RetailMediaPayload,
  type RetailMediaProduct
} from '../types/index.js';
import {
  callApi,
  RateLimitError,
  getVisitorId
} from '../utils/RetailMediaApi.js';

/**
 * The carousel pages three cards at a time, so it asks for enough products to
 * fill a few pages. The server returns fewer when the keyword ladder runs dry.
 */
const SPONSORED_PRODUCTS_COUNT = 10;

const request = async (
  container: PlacementId,
  searchQuery: string,
  categories: string[] | undefined,
  brandNames?: Record<string, string>
) => {
  updatePlacementKeyValueById(container, 'status', 'requested');
  updateMetricByKey(container, PlacementStatus.AdLoaded);

  const brand = $config.get()?.brand;
  const dsaBrand = DsaBrand.is(brand) ? brand : undefined;

  try {
    const payload: RetailMediaPayload = {
      zone: 'recommerce_search',
      visitorId: getVisitorId(),
      sponsoredProductsCount: SPONSORED_PRODUCTS_COUNT,
      searchTerm: searchQuery,
      ...(categories && categories.length > 0 && { categories }),
      ...(brandNames &&
        Object.keys(brandNames).length > 0 && {
          brandNames: Object.values(brandNames)
        }),
      debug: true,
      userAgent: navigator.userAgent,
      ...(dsaBrand && { dsaBrand })
    };
    const data = await callApi(payload);

    debugLog('retail_media -> API response:', data);

    updatePlacementKeyValueById(container, 'creativeType', 'native-retail');
    updatePlacementKeyValueById(container, 'status', 'loaded');

    renderSponsoredProductCarousel(
      container,
      data.sponsoredProducts,
      categories
    );
  } catch (error) {
    // Ignore rate-limited requests - the first request will handle rendering
    if (error instanceof RateLimitError) {
      debugLog('retail_media -> Rate-limited request ignored:', error.message);
      return;
    }
    debugLog('retail_media -> Error in request:', error);
    updateMetricByKey(container, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(container, 'status', 'error');
  }
};

export function triggerCarouselRequest(
  container: PlacementId,
  searchQuery: string | string[] | undefined,
  categories: string[] | undefined,
  brandNames?: Record<string, string>
) {
  const normalizedQuery = Array.isArray(searchQuery)
    ? searchQuery[0]
    : searchQuery;

  void request(container, normalizedQuery || '', categories, brandNames);
}

function renderSponsoredProductCarousel(
  placementId: PlacementId,
  products?: Array<RetailMediaProduct>,
  categories?: string[]
) {
  setTimeout(() => {
    const containerId = ContainerId.fromPlacementId(
      placementId,
      AdServer.Retailmedia
    );
    const slotElement = document.getElementById(containerId);
    if (!slotElement) {
      debugLog('retail_media -> Slot element not found:', containerId);
      return;
    }

    // Clear the slot. Note: the grid item is the search page's own
    // `.retail-media-ad-wrapper`, not this element, so column spanning is done
    // from CSS (see `.retail-media-ad-wrapper:has(.rm-carousel)`).
    slotElement.innerHTML = '';

    // Handle no-fill case
    if (!products || products.length === 0) {
      debugLog('retail_media -> No-fill case for placement:', placementId);
      updateMetricByKey(placementId, PlacementStatus.AdNoFill);
      updatePlacementKeyValueById(placementId, 'status', 'error');
      return;
    }

    // Create a container for the React component
    const container = document.createElement('div');
    slotElement.appendChild(container);

    // Render the React component into the container
    const root = createRoot(container);
    root.render(
      <SponsoredProductCarousel
        products={products}
        placementId={placementId}
        categories={categories}
      />
    );
  }, 1);
}
